import React, {useMemo} from "react"
import PropTypes from "prop-types";
import Tr from "translations/Translations";
import {makeStyles} from "@material-ui/core/styles";
import TextInput from "components/TextInput";
import INPUT_TYPE from "constants/INPUT_TYPE";
import {SELECT_VALUE} from "constants/VATItems";
import {InputAdornment} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import {createSelectItem} from "components/Select";
import getObjectValue from "libs/getObjectValue";
import {NetSumPriceVisible, PriceType, PriceTypeGrossFieldVisible, PriceTypeNetFieldVisible, PriceTypeVatFieldVisible} from "constants/PriceTypes";
import VatType from "constants/VatType";
import GridBlock from "components/GridBlock";
import SimpleSelect from "components/SimpleSelect";
import useDebouncedEffect from "hooks/useDebouncedEffect";
import ImportExportIcon from '@material-ui/icons/ImportExport';
import IconButton from "components/button/IconButton";
import clsx from "clsx";
import {simpleConfirm} from "libs/ConfirmNotification";
import toNumberType from "libs/toNumberType";
import HelpComponent from "components/HelpComponent";

const useStyle = makeStyles(theme => ({
    netValue: {
        width: 230,
        marginRight: theme.spacing(2)
    },
    vat: {
        width: 200,
    },
    grossValue: {
        width: 200,
        marginRight: theme.spacing(2)
    },
    currency: {
        width: 70
    },
    marginRight: {
        marginRight: theme.spacing(2)
    },
    text: {
        width: 250,
        marginLeft: theme.spacing(2)
    },
    gridBlock: {
        justifyContent: 'end'
    },
    marginTop: {
        marginTop: theme.spacing(1)
    }
}))


const VAT_TYPE_ITEMS = [
    createSelectItem("", Tr('select')),
    createSelectItem(VatType.NOT_APPLICABLE, Tr('not_applicable')),
    createSelectItem(VatType.EXEMPTED, Tr('released')),
    createSelectItem(VatType.REVERSED, Tr('reversed')),
    createSelectItem(VatType.STANDARD + "_0", "0%"),
    createSelectItem(VatType.STANDARD + "_3", "3%"),
    createSelectItem(VatType.STANDARD + "_5", "5%"),
    createSelectItem(VatType.STANDARD + "_7", "7%"),
    createSelectItem(VatType.STANDARD + "_8", "8%"),
    createSelectItem(VatType.STANDARD + "_23", "23%"),
    createSelectItem(VatType.MIXED, Tr('different_vat_rates')),
    createSelectItem(VatType.OTHER, Tr('other_vat_value')),
]

const isFilled = (value) => value !== null && value !== undefined && value !== '';

const calculateGrossValue = (values) => {
    let newGrossValue = null;
    if (isFilled(values.netValue) && !!values.vatType) {
        const netValue = parseFloat(values.netValue);
        if (!isNaN(netValue)) {
            switch (values.vatType) {
                case VatType.STANDARD:
                case VatType.OTHER:
                    if (values.vatValue != null) {
                        const vatValue = parseFloat(values.vatValue);
                        let vatMultipler = vatValue / 100
                        let newGross = netValue + netValue * vatMultipler;
                        if (!isNaN(newGross))
                            newGrossValue = newGross;
                    }
                    break
                case VatType.MIXED:
                    break;
                default:
                    newGrossValue = values.netValue;
            }
        }
    }
    return newGrossValue;
}

export const calcNetGros = (values, quantity, priceType) => {
    let sumNet = null;
    let sumGross = null;
    switch (priceType) {
        case PriceType.NET:
            if (isFilled(values.netValue))
                sumNet = parseFloat(values.netValue) * quantity.value;
            break;
        case PriceType.NET_VAT:
            if (isFilled(values.netValue)) {
                sumNet = parseFloat(values.netValue) * quantity.value;
                if (values.vatType) {
                    const gross = calculateGrossValue(values);
                    if (gross != null) {
                        sumGross = gross * quantity.value;
                    }
                }
            }
            break;
        case PriceType.NET_GROSS:
            if (values.vatType === VatType.MIXED) {
                if (values.grossValue != null) {
                    sumGross = parseFloat(values.grossValue) * quantity.value;
                }
                if (values.netValue != null) {
                    sumNet = parseFloat(values.netValue) * quantity.value;
                }
            } else {
                if (values.netValue != null) {
                    sumNet = parseFloat(values.netValue) * quantity.value;
                    if (values.vatType) {
                        const gross = calculateGrossValue(values);
                        if (gross != null) {
                            sumGross = gross * quantity.value;
                        }
                    }
                }
            }
            break;
        case PriceType.GROSS_VAT:
        case PriceType.GROSS:
            if (values.grossValue != null) {
                sumGross = parseFloat(values.grossValue) * quantity.value;
            }
            break;
        default:
    }
    let roundedGross = null;
    if (sumGross !== null) {
        roundedGross = (Math.round(sumGross * 100) / 100);
    }
    let roundedNet = null;
    if (sumNet !== null) {
        roundedNet = (Math.round(sumNet * 100) / 100);
    }
    return [roundedNet, roundedGross];
}


function OfferPriceInput(
    {
        id,
        formikProps,
        criterium,
        priceType,
        quantity,
        disabled
    }) {
    const classes = useStyle();
    const parsedQuantity = !!quantity ? { value: Number(toNumberType(quantity.value)), uom: quantity.uom } : null;

    const show = useMemo(() => ({
        netField: PriceTypeNetFieldVisible(priceType),
        grossField: PriceTypeGrossFieldVisible(priceType),
        vatField: PriceTypeVatFieldVisible(priceType),
        quantity: !!quantity,
        copyVat: PriceTypeVatFieldVisible(priceType) && id.startsWith("itemOffers") && formikProps.values.itemOffers.length > 1
        // eslint-disable-next-line
    }), [priceType, quantity, id])

    const keys = useMemo(() => ({
        netValue: id + ".value",
        grossValue: id + ".grossValue",
        vatType: id + ".vatType",
        vatValue: id + ".vatValue",
        totalGross: id + ".totalGross",
        totalNet: id + ".totalNet",
        parameterOffers: id + ".parameterOffers",
        quantityDisabled: id + ".quantityDisabled"
    }), [id]);

    const values = {
        netValue: show.netField ? getObjectValue(keys.netValue, formikProps.values, null) : null,
        grossValue: show.grossField ? getObjectValue(keys.grossValue, formikProps.values, null) : null,
        vatType: show.vatField ? getObjectValue(keys.vatType, formikProps.values, null) : null,
        vatValue: show.vatField ? getObjectValue(keys.vatValue, formikProps.values, null) : null,
        parameterOffers: getObjectValue(keys.parameterOffers, formikProps.values, null),
        quantityDisabled: getObjectValue(keys.quantityDisabled, formikProps.values, false)
    }


    useDebouncedEffect(() => {
        const offer = getObjectValue(id, formikProps.values);
        let grossValue = offer.grossValue;
        let totalGross = offer.totalGross;
        let totalNet = offer.totalNet;
        if (priceType === PriceType.NET_GROSS) {
            grossValue = calculateGrossValue(values);
            if (grossValue)
                grossValue = Math.round(grossValue * 100) / 100;
        }
        if (show.quantity) {
            const [roundedNet, roundedGross] = calcNetGros(values, parsedQuantity, priceType);
            totalGross = roundedGross;
            totalNet = roundedNet;
        }
        formikProps.setFieldValue(id, {
            ...offer,
            grossValue,
            totalGross,
            totalNet
        }, false);

        if (!!criterium.optionalItemId && !!criterium.copyValuesFromBaseItem) {
            const formikValues = formikProps.values;
            const optionalItemIndex = formikValues.itemOffers.map(x => x.criterium.identity).indexOf(criterium.optionalItemId);
            const optionalItemId = `itemOffers.${optionalItemIndex}`;

            const optionalItem = getObjectValue(optionalItemId, formikProps.values, null);
            if (optionalItem) {
                formikProps.setFieldValue(optionalItemId, {
                    ...optionalItem,
                    value: values.netValue,
                    vatType: values.vatType,
                    vatValue: values.vatValue,
                    grossValue: values.grossValue
                });
            }
        }

    }, {timeout: 500, ignoreInitialCall: true}, [values.netValue, values.vatValue, values.vatType, quantity])

    useDebouncedEffect(() => {
        const offer = getObjectValue(id, formikProps.values);
        if ((priceType === PriceType.GROSS || priceType === PriceType.GROSS_VAT || (priceType === PriceType.NET_GROSS && offer.vatType === VatType.MIXED)) && show.quantity) {
            const netAndGross = calcNetGros(values, parsedQuantity, priceType);
            const totalGross = netAndGross[1];
            formikProps.setFieldValue(id, {
                ...offer,
                totalGross
            }, false);

            if (!!criterium.optionalItemId && !!criterium.copyValuesFromBaseItem && offer.grossValue) {
                const formikValues = formikProps.values;
                const optionalItemIndex = formikValues.itemOffers.map(x => x.criterium.identity).indexOf(criterium.optionalItemId);
                const optionalItemId = `itemOffers.${optionalItemIndex}`;

                const optionalItem = getObjectValue(optionalItemId, formikProps.values, null);
                if (optionalItem) {
                    formikProps.setFieldValue(optionalItemId, {
                        ...optionalItem,
                        grossValue: values.grossValue
                    });
                }
            }
        }
    }, {timeout: show.quantity ? 500 : 10000000, ignoreInitialCall: true}, [values.grossValue])

    useDebouncedEffect(() => {
        if (!!criterium.optionalItemId && !!criterium.copyValuesFromBaseItem) {
            const formikValues = formikProps.values;
            const optionalItemIndex = formikValues.itemOffers.map(x => x.criterium.identity).indexOf(criterium.optionalItemId);
            const optionalItemId = `itemOffers.${optionalItemIndex}`;

            const optionalItem = getObjectValue(optionalItemId, formikProps.values, null);
            if (optionalItem) {
                const parameterOffers = optionalItem.parameterOffers;
                formikProps.setFieldValue(`${optionalItemId}.parameterOffers`, parameterOffers.map(x => {
                    const baseParameterOffer = values.parameterOffers.filter(o => o.parameterId === x.parameterId)[0];
                    return {
                        ...x,
                        offerValue: baseParameterOffer.offerValue
                    }
                }));
            }
        }

    }, {timeout: show.quantity ? 500 : 10000000, ignoreInitialCall: true}, [values.parameterOffers])

    const vatError = getObjectValue(keys.vatType, formikProps.touched, false) ? getObjectValue(keys.vatType, formikProps.errors, null) : null;

    const closeTextInput = () => {
        formikProps.setFieldValue(keys.vatValue, null);
        formikProps.setFieldValue(keys.vatType, null);
    }

    const handleVatTypeChange = (e) => {
        const selValue = e.target.value;
        if (selValue === "") {
            formikProps.setFieldValue(keys.vatType, null);
            formikProps.setFieldValue(keys.vatValue, null);
        } else if (selValue.startsWith(VatType.STANDARD)) {
            formikProps.setFieldValue(keys.vatType, VatType.STANDARD);
            formikProps.setFieldValue(keys.vatValue, selValue.substring(VatType.STANDARD.length + 1));
        } else {
            formikProps.setFieldValue(keys.vatType, selValue);
            formikProps.setFieldValue(keys.vatValue, null);
        }
    }

    const getSelectValue = () => {
        let val = "";
        if (values.vatType === VatType.STANDARD || values.vatType === VatType.OTHER) {
            if (values.vatValue !== null) {
                val = values.vatType + "_" + values.vatValue
            }
        } else {
            val = values.vatType;
        }
        return val;
    }

    const copyVats = () => {
        const values = formikProps.values;
        const vatValue = getObjectValue(keys.vatValue, values, null);
        const vatType = getObjectValue(keys.vatType, values, null);
        formikProps.setValues({
            ...values,
            itemOffers: values.itemOffers.map(x => {
                if (x.criterium.offerVatPredefined)
                    return x;
                else
                    return {...x, vatValue, vatType};
            })
        })
    }

    return (
        <>
            <GridBlock className={classes.gridBlock}>
                {
                    show.netField && <TextInput
                        className={classes.netValue}
                        id={keys.netValue}
                        formikProps={formikProps}
                        label={Tr('enter_net_value')}
                        type={INPUT_TYPE.NUMBER_19}
                        fullWidth={false}
                        disabled={disabled || criterium.offerValuesPredefined}
                    />
                }

                {show.vatField && <>
                    {
                        values.vatType === SELECT_VALUE.OTHER ?
                            <TextInput
                                className={clsx(classes.vat, {[classes.marginRight]: !show.copyVat})}
                                id={keys.vatValue}
                                formikProps={formikProps}
                                label={Tr('other_vat_value')}
                                type={INPUT_TYPE.NUMBER_POSITIVE_AMOUNT}
                                fullWidth={false}
                                endAdornment={
                                    <InputAdornment position='end'>
                                        %
                                        {!criterium.offerVatPredefined && <IconButton
                                            onClick={closeTextInput}
                                            className={classes.iconButton}
                                            Icon={CloseIcon}
                                        />}
                                    </InputAdornment>
                                }
                                disabled={disabled || criterium.offerVatPredefined}
                            />
                            :
                            <SimpleSelect
                                className={clsx(classes.vat, {[classes.marginRight]: !show.copyVat})}
                                items={VAT_TYPE_ITEMS}
                                label={Tr('choose_vat_value')}
                                value={getSelectValue()}
                                onChange={handleVatTypeChange}
                                error={vatError}
                                disabled={disabled || criterium.offerVatPredefined}
                            />
                    }
                </>}

                {show.copyVat &&
                    <IconButton
                        tooltip={Tr("vat_copy_to_items")}
                        Icon={ImportExportIcon}
                        onClick={() => simpleConfirm("vat_copy_to_items_confirm", copyVats)}
                        disabled={criterium.offerVatPredefined}
                    />
                }

                {
                    show.grossField && <TextInput
                        className={classes.grossValue}
                        id={keys.grossValue}
                        formikProps={formikProps}
                        label={Tr('gross_price')}
                        type={INPUT_TYPE.NUMBER_19}
                        step="0.01"
                        fullWidth={false}
                        disabled={disabled || (priceType === PriceType.NET_GROSS && values.vatType !== VatType.MIXED) || criterium.offerValuesPredefined}
                    />
                }

                {
                    show.quantity &&
                    <TextInput
                        className={classes.grossValue}
                        id={NetSumPriceVisible(priceType) ? keys.totalNet : keys.totalGross}
                        formikProps={formikProps}
                        label={Tr(NetSumPriceVisible(priceType) ? 'items_value_net' : 'items_value_gross')}
                        type={INPUT_TYPE.NUMBER_19}
                        step="0.01"
                        fullWidth={false}
                        disabled
                    />
                }

                <SimpleSelect
                    className={classes.currency}
                    label={Tr('currency')}
                    fullWidth={false}
                    items={[createSelectItem("PLN", "PLN")]}
                    value="PLN"
                    disabled
                />
            </GridBlock>
            {getObjectValue(`${keys.parameterOffers}`, formikProps.values, []).length > 0 &&
                <GridBlock className={clsx(classes.gridBlock, classes.marginTop)}>
                    {getObjectValue(`${keys.parameterOffers}`, formikProps.values, []).map((po, index) => {
                        return (
                        <HelpComponent key={`po_${index}`} dangerouslySetTextHTML={getObjectValue(`${keys.parameterOffers}.${index}.parameterDescription`, formikProps.values, Tr('no_description'))}>
                            <TextInput
                                className={classes.text}
                                id={`${keys.parameterOffers}.${index}.offerValue`}
                                formikProps={formikProps}
                                label={getObjectValue(`${keys.parameterOffers}.${index}.parameterName`, formikProps.values)}
                                multiline
                                disabled={disabled || !!values.quantityDisabled}
                            />
                        </HelpComponent>)
                        }
                    )}
                </GridBlock>
            }
        </>
    )


}

OfferPriceInput.propTypes = {
    id: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired
}

export default OfferPriceInput;